import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { OrderTableData } from '@teinor/erp/prices/quote/totals'
import { ButtonPrimary } from '../../../../../../../baseComponents/Button'
import { CalculateParamsTy } from '../../../../../../../baseComponents/Displays/DisplayPrice/types'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getPreparePricesConfigParam } from '../../../../../../../utils/reducers/getters'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useOrderFinancialRowTypesDict from '../../../../../../../utils/hooks/orders/useOrderFinancialRowTypesDict'
import { recalculateAllQuoteOrderRows } from '@teinor/erp/prices/quote/calculate'
import { flushSync } from 'react-dom'
import { financialRowTypesInversed, HeaderDataCalculate } from '@teinor/erp/types/company/order/general'
import { parseOrderTableData } from '../../../../../../../specificComponents/inputTableLayouts/order/OrderQuoteLayout/function'

type  QuoteStepHeaderFinancialRowsProps =  InputTableLeftTabsProps & QuoteCreateStepHeaderFinancialRowLogicProps & {
    tableData?: OrderTableData
}

const QuoteStepHeaderFinancialRows: React.FC<QuoteStepHeaderFinancialRowsProps> = ({ headerData, tableData, setCompleteData, orderHeaderURLs}) => {
    const { t } = useTwinTranslation()
    const currencyHeaderId = headerData.CurrencyId
    const financialRows = headerData.OrderFinancialRows
    const { myHandleAdd, myHandleChange, total, deleteFinancialRow } = useQuoteCreateStepHeaderFinancialRowLogic({ setCompleteData, tableData, headerData, orderHeaderURLs })

    const financialRowTypesDict = useOrderFinancialRowTypesDict()

    const financialRowsSections: FormRender<any> = []
    if (financialRows?.length) {
        for (let key in financialRows) {
            const financialRow = financialRows[key]
            const type = parseInt(financialRow?.financialRowType)
            let maxDiscount: any = {}
            if (type === financialRowTypesInversed['discountWithoutCosts'] || type === financialRowTypesInversed['discountWithCosts']) {
                maxDiscount = { maxPercentageDiscount: 100, maxTotalDiscount: total, maxUnitDiscount: 0 }
            } else {
                maxDiscount = { maxPercentageDiscount: Infinity, maxTotalDiscount: Infinity, maxUnitDiscount: 0 }
            }
            financialRowsSections.push({
                cols: 2,
                title: <div className='flex items-center justify-between'>{t('discountOrRecharge', 'Descuento o recargo') + ' ' + (parseInt(key) + 1)} <TwinIcon onClick={() => deleteFinancialRow(key)} icon={faTimes} className='cursor-pointer hover:text-red-BA' size='lg' /></div>,
                elements: [
                    {
                        name: key + '-discount',
                        component: 'InputDiscount',
                        label: t('quantity', 'Cantidad'),
                        value: { typeDiscount: financialRow?.calculationType, discount: financialRow?.quantity },
                        CurrencyId: currencyHeaderId,
                        maxDiscount
                    },
                    {
                        name: key + '-financialRowType',
                        component: 'CustomSelect',
                        label: t('type', 'Tipo'),
                        items: financialRowTypesDict,
                        value: financialRow?.financialRowType === financialRowTypesInversed['discountForTotalFooter'] ? financialRowTypesInversed['discountWithCosts'] : financialRow?.financialRowType,
                        readOnly: financialRow?.financialRowType === financialRowTypesInversed['discountForTotalFooter']
                    },
                    {
                        name: key + '-beforeTax',
                        component: 'Checkbox',
                        label: t('beforeTax', 'Antes de impuestos'),
                        checked: financialRow?.beforeTax,
                        className: returnClassOfSpanTemplate(2),
                        disabled: financialRow?.financialRowType === financialRowTypesInversed['discountForTotalFooter']
                    },
                ]
            })
        }
    }
    const sections: FormRender<any> = [
        ...financialRowsSections,
        {
            cols: 1,
            elements: [
                {
                    name: 'add',
                    component: 'Custom',
                    render: () => <ButtonPrimary onClick={myHandleAdd} type='button'>{t('add', 'Añadir')}</ButtonPrimary>,
                },
            ]
        }

    ]
    return (
        <InputTableLeftColumnForm form={sections} headerData={headerData} handleChange={myHandleChange} />
    )
}

interface QuoteCreateStepHeaderFinancialRowLogicProps {
    setCompleteData: SetCompleteDataTy
    headerData: TwinDictionary
    tableData?: OrderTableData
    orderHeaderURLs: OrderHeaderURLs
}

export const useQuoteCreateStepHeaderFinancialRowLogic = ({ headerData, tableData, setCompleteData, orderHeaderURLs }: QuoteCreateStepHeaderFinancialRowLogicProps) => {
    const params = useParams<{ id: string }>()
    const id = params.id

    const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
    const headerDataCalculate: HeaderDataCalculate = {
        OrderCosts: headerData?.OrderCosts,
        ParamsRoundingDecimals: calculateParams,
        OrderShipping: headerData?.OrderShipping,
        isFinished: headerData?.isFinished,
        OrderAccounts: headerData?.OrderAccounts
    }
    const parsedTableData = parseOrderTableData(tableData || [], headerDataCalculate)
    const total = parsedTableData.totals.total
    const myHandleAdd = useCallback(async () => {
        const financialRow = await twinFetchPostJSON(orderHeaderURLs.createFinancialRow, {
            quantity: 0,
            OrderId: id
        })
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData?.header?.OrderFinancialRows?.push(financialRow)
            return { ...myData }
        })
    }, [id, setCompleteData, orderHeaderURLs])

    const deleteFinancialRow = useCallback(async (key: string) => {
        flushSync(async () => {
            let financialRow: TwinDictionary = {}
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                financialRow = myData.header.OrderFinancialRows[key]
                myData.header.OrderFinancialRows.splice(key, 1)
                recalculateAllQuoteOrderRows(myData)
                return { ...myData }
            })
            await twinFetchPostJSON(orderHeaderURLs.deleteFinancialRow, {
                id: financialRow.id
            })
        })
    }, [setCompleteData, orderHeaderURLs])
    const myHandleChange = useCallback(async (id: string, field: string, value: any) => {
        flushSync(async () => {
            let financialRow: TwinDictionary = {}
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const [key, myField] = field.split('-')
                const myData = JSON.parse(JSON.stringify(old))
                if (myField === 'financialRowType') {
                    myData.header.OrderFinancialRows[key]['quantity'] = ''
                }
                if (myField === 'discount' && myData.header.OrderFinancialRows[key]) {
                    myData.header.OrderFinancialRows[key]['quantity'] = value.discount
                    myData.header.OrderFinancialRows[key]['calculationType'] = value.typeDiscount
                } else if (myData.header.OrderFinancialRows[key]) {
                    myData.header.OrderFinancialRows[key][myField] = value
                }
                const type = parseInt(myData.header.OrderFinancialRows[key].financialRowType)

                if (type === financialRowTypesInversed['rechargeWithCosts'] || type === financialRowTypesInversed['rechargeWithoutCosts']) {
                    myData.header.OrderFinancialRows[key].beforeTax = true
                } else if (type === financialRowTypesInversed['rechargeFinancial']) {
                    myData.header.OrderFinancialRows[key].beforeTax = false
                }
                financialRow = myData.header.OrderFinancialRows[key]
                recalculateAllQuoteOrderRows(myData)
                return { ...myData }
            })
            await twinFetchPostJSON(orderHeaderURLs.updateFinancialRow, { ...financialRow })
        })
    }, [setCompleteData, orderHeaderURLs])

    return { myHandleAdd, myHandleChange, total, deleteFinancialRow }
}

export default QuoteStepHeaderFinancialRows